import { baseApi } from "./baseApi";
import type {
  GetViewingsBoardResponse,
  GetViewingsListParams,
  GetViewingsListResponse,
  ListViewingsParams,
  ViewingsData,
} from "../../pages/Crm/components/Viewings/utils/types";

export const viewingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getViewingsBoard: builder.query<
      GetViewingsBoardResponse,
      ListViewingsParams
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.page) searchParams.append("page", String(params.page));
        if (params.limit) searchParams.append("limit", String(params.limit));
        if (params.search) searchParams.append("search", params.search);
        if (params.sort_key)
          searchParams.append("sort_key", params.sort_key);
        if (params.sort_order)
          searchParams.append("sort_order", params.sort_order);
        if (params.scheduled_at_from)
          searchParams.append("scheduled_at_from", params.scheduled_at_from);
        if (params.scheduled_at_to)
          searchParams.append("scheduled_at_to", params.scheduled_at_to);
        if (params.created_at_from)
          searchParams.append("created_at_from", params.created_at_from);
        if (params.created_at_to)
          searchParams.append("created_at_to", params.created_at_to);
        if (params.property_ids?.length)
          searchParams.append("property_ids", params.property_ids.join(","));
        if (params.has_no_property)
          searchParams.append("has_no_property", "true");
        if (params.attendee_response_status?.length)
          searchParams.append(
            "attendee_response_status",
            params.attendee_response_status.join(",")
          );

        const qs = searchParams.toString();
        return {
          url: `/viewings/board${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Viewings", id: "ViewingsBoard" }],
      keepUnusedDataFor: 0,
    }),
    getViewingsList: builder.query<
      GetViewingsListResponse,
      GetViewingsListParams
    >({
      query: ({
        status,
        page,
        limit,
        search,
        sort_key,
        sort_order,
        attendee_response_status,
        property_ids,
        has_no_property,
        scheduled_at_from,
        scheduled_at_to,
        created_at_from,
        created_at_to,
      }) => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (page) params.set("page", String(page));
        if (limit) params.set("limit", String(limit));
        if (search) params.set("search", search);
        if (sort_key) params.set("sort_key", sort_key);
        if (sort_order) params.set("sort_order", sort_order);
        if (attendee_response_status?.length) {
          params.set(
            "attendee_response_status",
            attendee_response_status?.join(",")
          );
        }
        if (property_ids?.length) {
          params.set("property_ids", property_ids?.join(","));
        }
        if (has_no_property) {
          params.set("has_no_property", "true");
        }
        if (scheduled_at_from)
          params.set("scheduled_at_from", scheduled_at_from);
        if (scheduled_at_to) params.set("scheduled_at_to", scheduled_at_to);
        if (created_at_from) params.set("created_at_from", created_at_from);
        if (created_at_to) params.set("created_at_to", created_at_to);
        return {
          url: `/viewings/list?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [{ type: "Viewings", id: "ViewingsList" }],
    }),
    getViewingById: builder.query<ViewingsData, string>({
      query: (viewingId) => ({
        url: `/viewings/info/${viewingId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetViewingsBoardQuery,
  useLazyGetViewingsBoardQuery,
  useGetViewingsListQuery,
  useLazyGetViewingsListQuery,
  useLazyGetViewingByIdQuery,
} = viewingsApi;
